import RAPIER from '@dimforge/rapier3d-compat';
import { SIM } from '../../config/tuning';
import type { TerrainProfile } from './profile';

export const CHUNK_LENGTH = 50;
export const SAMPLE_STEP = 0.5;
export const SAMPLES_PER_CHUNK = CHUNK_LENGTH / SAMPLE_STEP + 1;

/** Heights along x for chunk `index`, sampled every SAMPLE_STEP, both ends inclusive. */
export function sampleChunk(profile: TerrainProfile, index: number): Float32Array {
  const x0 = index * CHUNK_LENGTH;
  const heights = new Float32Array(SAMPLES_PER_CHUNK);
  for (let i = 0; i < SAMPLES_PER_CHUNK; i++) heights[i] = profile.heightAt(x0 + i * SAMPLE_STEP);
  return heights;
}

export interface TerrainChunkBody {
  index: number;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
}

/**
 * A fixed heightfield collider covering [x0, x0 + CHUNK_LENGTH] along X and the terrain slab along Z.
 * Rapier heightfields are centred on their body and span `scale` along X and Z; heights are stored
 * column-major with rows along Z and columns along X (verified in tests/sim/terrain-collider.test.ts).
 */
export function createChunkBody(
  world: RAPIER.World,
  index: number,
  heights: Float32Array,
  friction: number,
): TerrainChunkBody {
  const ncols = heights.length - 1; // cells along X
  const nrows = 1; // one cell across the slab depth
  const matrix = new Float32Array((nrows + 1) * (ncols + 1));
  // Column-major: element (row r, col c) lives at c * (nrows + 1) + r.
  for (let c = 0; c <= ncols; c++) {
    matrix[c * (nrows + 1)] = heights[c];
    matrix[c * (nrows + 1) + 1] = heights[c];
  }
  const x0 = index * CHUNK_LENGTH;
  const body = world.createRigidBody(
    RAPIER.RigidBodyDesc.fixed().setTranslation(x0 + CHUNK_LENGTH / 2, 0, 0),
  );
  const desc = RAPIER.ColliderDesc.heightfield(
    nrows,
    ncols,
    matrix,
    { x: CHUNK_LENGTH, y: 1, z: SIM.terrainHalfDepth * 2 },
    RAPIER.HeightFieldFlags.FIX_INTERNAL_EDGES,
  ).setFriction(friction);
  const collider = world.createCollider(desc, body);
  return { index, body, collider };
}
